/* eslint-disable i18next/no-literal-string -- a code-only module: board codes and role codes; it renders nothing */
// "Set as commander" / "Set as partner" (Wave 2). Pure: the command zone a
// swap lands on and the card changes that go with it, as ONE change set.
//
// Eligibility is NEVER decided here: it comes from the API's
// POST /decks/commander-candidates (card_can_be_commander and
// card_can_have_partner in mtg-api app/validation.py), the same check the
// paste flow's commander picker uses. This module only arranges the zone.
import { commandZone, extractCommanders, frontKey } from '../../../lib/decklist'
import type { CardChange, EditorBoard } from './editorHistory'

/** What the candidates endpoint said about one card (absent: not eligible). */
export interface CommanderEligibility {
  /** card_can_be_commander. */
  commander: boolean
  /** card_can_have_partner: it can take (or be) a second command-zone card. */
  partner: boolean
}

export type ZoneRole = 'commander' | 'partner'

export interface CommandZoneSwap {
  /** The command zone after the swap (commander first). */
  commanders: string[]
  /** The card changes (see commandZoneSwap). */
  changes: CardChange[]
  /** The command-zone cards that moved into the main deck. */
  displaced: string[]
}

/**
 * Which roles a card may take, given what the endpoint said about it and
 * about the current command zone. `eligibility(name)` is undefined while the
 * answer is not in (nothing is offered then) and null for a card the endpoint
 * did not list (not eligible).
 *
 * - commander: the card itself can be a commander.
 * - partner: it can be a commander AND can have a partner, and the current
 *   commander (the first in the zone) can have one too. Only the per-card
 *   check exists client-side; a pair the rules do not allow together is the
 *   re-rate's validation to report (see the report's notes).
 */
export function allowedRoles(
  name: string,
  commanders: readonly string[],
  eligibility: (name: string) => CommanderEligibility | null | undefined,
): ZoneRole[] {
  const key = frontKey(name)
  if (commanders.some(c => frontKey(c) === key)) return []
  const self = eligibility(name)
  if (!self || !self.commander) return []
  const out: ZoneRole[] = ['commander']
  const primary = commanders[0]
  if (primary && self.partner) {
    const p = eligibility(primary)
    if (p && p.partner) out.push('partner')
  }
  return out
}

/**
 * The swap: `name` (from `board`) takes `role`.
 *
 * - commander: it replaces the first command-zone card. A partner stays only
 *   when the new commander can have one (and the partner itself still can);
 *   otherwise the partner also moves to the main deck.
 * - partner: it becomes the second command-zone card, replacing any partner.
 *
 * The editor's model keeps the command zone INSIDE the main deck's rows
 * (locked; the decklist's "// Commander" lines are deck cards), so a card
 * already in the main deck does not move: it only locks or unlocks. A card
 * taken from the Maybeboard leaves it (one copy) and joins the main deck; a
 * displaced command-zone card the main deck does not hold yet (a legacy list
 * that left it out) joins it as one copy. `inMain` answers from the main
 * deck's rows. The caller pushes the result as ONE history entry (the zone
 * change with these changes), so one Undo puts everything back; the change
 * list can be empty (a swap within the main deck).
 */
export function commandZoneSwap(
  name: string,
  board: EditorBoard,
  role: ZoneRole,
  commanders: readonly string[],
  eligibility: (name: string) => CommanderEligibility | null | undefined,
  inMain: (name: string) => boolean,
): CommandZoneSwap {
  const [primary, ...rest] = commanders
  let next: string[]
  let displaced: string[]
  if (role === 'partner') {
    next = primary ? [primary, name] : [name]
    displaced = rest
  } else {
    const self = eligibility(name)
    const keep = rest.filter(p => !!self?.partner && !!eligibility(p)?.partner).slice(0, 1)
    next = [name, ...keep]
    displaced = [primary, ...rest.filter(p => !keep.includes(p))].filter((n): n is string => !!n)
  }
  const changes: CardChange[] = []
  if (board === 'maybe') changes.push({ board: 'maybe', name, delta: -1 })
  if (board === 'maybe' && !inMain(name)) changes.push({ board: 'main', name, delta: 1 })
  for (const d of displaced) if (!inMain(d)) changes.push({ board: 'main', name: d, delta: 1 })
  return { commanders: next, changes, displaced }
}

/** The candidates request's decklist: the card and the command zone, one copy each. */
export function eligibilityDecklist(names: readonly string[]): string {
  const seen = new Set<string>()
  const lines: string[] = []
  for (const n of names) {
    const k = frontKey(n)
    if (!n.trim() || seen.has(k)) continue
    seen.add(k)
    lines.push(`1 ${n.trim()}`)
  }
  return lines.join('\n')
}

/** The row's commander and partner columns, as a save writes them. */
export interface ZoneFields {
  commander: string | null
  partner: string | null
}

const sameZone = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((n, i) => frontKey(n) === frontKey(b[i]))

/**
 * The commander / partner an INSTANT save (the editor's save queue) writes
 * with `text`, or undefined when the write leaves them alone.
 *
 * The command zone lives IN the text (the "// Commander" section the editor
 * serializes), so the zone a write carries is derived from the text it
 * writes, never from screen state: a queued write replayed after its screen
 * is gone, a draft restore and an undo all carry their own zone. It is sent
 * only when it differs from the zone of `expected` (the stored text the
 * compare-and-swap expects), so an ordinary edit, and a legacy row whose text
 * tags more cards than its columns hold, keep the columns exactly as stored.
 */
export function zoneWriteFor(text: string, expected: string | null): ZoneFields | undefined {
  const next = extractCommanders(text)
  if (sameZone(next, extractCommanders(expected ?? ''))) return undefined
  return { commander: next[0] ?? null, partner: next[1] ?? null }
}

/**
 * The commander / partner an editor session stands for, for the re-rate's
 * request and save and for the Coach: the row's own fields while the
 * session's zone is still the row's (spelling and legacy partner state kept
 * exactly), else the session's zone (commander first, then partner).
 */
export function sessionZoneFields(
  zone: readonly string[],
  row: { decklist?: string | null; commander: string | null; partner?: string | null },
): ZoneFields {
  if (sameZone(zone, commandZone(row.decklist ?? null, row.commander, row.partner))) {
    return { commander: row.commander, partner: row.partner ?? null }
  }
  return { commander: zone[0] ?? null, partner: zone[1] ?? null }
}
